import { WidgetResult } from '../../core/hooks/useAnalytics';
import { APIFeatures, WidgetFeatures } from '../../core/modules/features';
import setupModelWidget from '../../core/modules/setupModelWidget/setupModelWidget';
import { setupSeasonalWidget } from '../../core/modules/setupSeasonalWidget/setupSeasonalWidget';
import { Deal } from '../../core/types/Deal';
import type { InitialiserResult } from '../../core/types/InitialiserResult';
import type { ReviewData } from '../../core/types/ModelInfo';
import { WidgetInitialiserOptions } from '../../core/types/WidgetInitialiserOptions';
import { WidgetProps } from '../../core/types/WidgetProps';
import { WidgetType } from '../../core/types/WidgetType';
import { getFilteredUrlParams } from '../../utils/getFilteredUrlParams';
import getUrlParameters from '../../utils/getUrlParameters';
import { getSkip } from '../getSkip';

import { getWidgetFromDisplayTypeOrWidgetType } from './getWidgetFromDisplayTypeOrWidgetType';

/**
 * Get the widget initialiser
 */
export const getWidgetInitialiser = async (
  options: WidgetInitialiserOptions,
): Promise<InitialiserResult> => {
  const {
    editorial,
    placeholder,
    defaultParams,
    index,
    site,
    territory,
    localiser,
    keywords,
    getWidgetResponse,
    getModelOffersResponse,
    getSeasonalResponse,
    getAiredaleFeeds,
    getModelSuggestions,
    postForWidgetResponse,
    articleId,
    articleName,
    articleUrl,
    articleType,
    origin,
    dataLinkMerchant,
  } = options;
  let { attributes } = options;

  const whitelist = getFilteredUrlParams(articleUrl);
  const urlParams = getUrlParameters(whitelist);
  const widget = getWidgetFromDisplayTypeOrWidgetType(
    attributes.displayType,
    attributes.widgetType,
  );

  attributes = {
    ...(defaultParams as DOMStringMap), // can't assign our types into DOMStringMap
    ...attributes,
    ...urlParams,
  };

  const apiFeatures = new APIFeatures(
    attributes,
    widget,
    editorial,
    localiser,
    site,
    territory,
    keywords,
    origin,
  );
  const widgetFeatures = new WidgetFeatures(
    attributes,
    widget,
    editorial,
    localiser,
    site,
    territory,
    keywords,
  );
  /* Get the widget options - params that only affect the widget & not the API data
  Get the whole object & destructure separately so we can later assign all the
  features without calling getWidgetFeatures again */
  const features = widgetFeatures.getFeatures('id');
  const params = {
    ...apiFeatures.getFeatures('value'),
    ...apiFeatures.getLabels(),
  };

  const skipWidget = getSkip(features, options, widget);

  const props = {
    attributes,
    getWidgetResponse,
    getModelOffersResponse,
    getSeasonalResponse,
    getAiredaleFeeds,
    getModelSuggestions,
    postForWidgetResponse,
    widget,
    editorial,
    placeholder: placeholder ?? null,
    site,
    territory,
    localiser,
    language: localiser.language,
    translate: localiser.translate.bind(localiser),
    index,
    articleId: articleId ?? '',
    articleName,
    articleUrl,
    articleType,
    keywords,
    dealData: { deals: [] as Deal[], totalDeals: 0, dealType: '', modelInfo: {} },
    reviews: [] as ReviewData[],
    ...features,
    params,
    widgetType: widget.type,
    dataLinkMerchant,
  } as WidgetProps; // TODO(Petr): HAWK-7003 Remove unsafe type assertion

  if (skipWidget) {
    return {
      props,
      result: WidgetResult.SKIPPED,
    };
  }

  // TODO(Petr): HAWK-4478 Widget specific, move somewhere else
  const setup = widget.type === WidgetType.SEASONAL ? setupSeasonalWidget : setupModelWidget;

  const initialiserResult = await setup({
    props,
    apiFeatures,
    widgetFeatures,
    localiser,
    features,
  });

  return initialiserResult;
};
